<template lang="pug">
.y_.j_(ref="exportContentRef")
  article.l
    h1.r.q
      upload-icon
      | {{ t("export-shader") }}

    section.n
      h2.r.q
        file-icon
        | {{ t("result") }}
      div(style="display: flex; gap: 1rem")
        glow-button(@click="copyExportContent")
          copy-icon
          | {{ t("copy-text") }}
        |
        glow-button(:download="currentShader.name + '.glsl'", :href="fileDownloadLink")
          download-icon(style="color: white")
          | {{ t("download-file") }}
      .t.b
        textarea.z_(readonly, :value="exportContent", @click="onTextareaClick")

    section.m
      h2.r.q
        gear-icon
        | {{ t("settings") }}
      .o(style="flex-flow: column")
        label
          input(type="checkbox", v-model="expandIncludes", @change="generateShader")
          | {{ t("expand") }}
          |
          code #include
          |
          | {{ t("directives") }}
        label
          //- @todo
          input(type="checkbox", v-model="noAngleIncludes", @change="generateShader")
          | {{ t("replace-includes-0") }}
          |
          code(style="text-wrap: nowrap") #include &lt;...&gt;
          | {{ " " }}{{ t("replace-includes-1") }}{{ " " }}
          code(style="text-wrap: nowrap") #include "..."
    section.m
      h2.r.q
        pencil-icon
        | {{ t("rename-symbols") }}
      section.n
        h3.r {{ t("inputs") }}
        .o
          label(v-for="i in inputList")
            ._a {{ i }}
            .t.b
              input.k(@input="e => renameSymbol(i, e)", :value="renameMap.get(i)")
      section.n
        h3.r {{ t("uniforms") }}
        .o
          label(v-for="i in uniformList")
            ._a {{ i }}
            .t.b
              input.k(@input="e => renameSymbol(i, e)", :value="renameMap.get(i)")
</template>

<script lang="ts">import { shallowRef as _shallowRef } from 'vue';

// @note module scope, so settings are saved during session

let expandIncludes = _shallowRef<boolean>(false);
let noAngleIncludes = _shallowRef<boolean>(false);

const renameMap = new Map();
</script>

<script setup lang="ts">import { shallowRef as _shallowRef } from 'vue';

import { currentShader } from "@/App.vue";
import UploadIcon from "octicons:upload";
import DownloadIcon from "octicons:download";
import CopyIcon from "octicons:copy";
import GearIcon from "octicons:gear";
import PencilIcon from "octicons:pencil";
import FileIcon from "octicons:file";

import { useToast } from "@/composition/useToast";
import type { Uri } from "monaco-editor";

import { useI18n } from "petite-vue-i18n";
import GlowButton from "../MenuBar/Button.vue";

const { t, locale } = useI18n();

let exportContentRef = _shallowRef<HTMLDivElement>();

let fileDownloadLink = _shallowRef<string>();
let exportContent = _shallowRef<string>("");

const generateShader = async () => {
  const [{ getModel, processIncludes, dependencyRegex }, { parse, generate }, { renameBindings }] =
    await Promise.all([
      import("@/editor"),
      import("@shaderfrog/glsl-parser"),
      import("@shaderfrog/glsl-parser/utils"),
    ]);

  let codeLines = getModel().getLinesContent();

  if (!expandIncludes.value && noAngleIncludes.value) {
    // @note instance it without global flag, because it won't work properly for test()
    let rg = new RegExp(dependencyRegex, "");
    codeLines.forEach((line, index, arr) => {
      if (rg.test(line)) {
        console.log(line);
        arr[index] = line.replace(/<|>/g, '"');
      }
    });
  }

  let code = expandIncludes.value ? await processIncludes(codeLines) : codeLines.join("\n");

  if (renameMap.size) {
    let ast = parse(code, { includeLocation: true });

    console.log(ast);

    renameBindings(ast.scopes[0], (name, node) => {
      let newName = renameMap.get(name);

      if (newName) return newName;

      return name;
    });

    code = generate(ast);
  }

  exportContent.value = code;
  fileDownloadLink.value = URL.createObjectURL(new Blob([code]));
};

generateShader();

// @note offer only inputs rename, since user can rename outputs by themselves
let inputList = ["uv"];
// @todo report, sometimes non reactive vars break for some reason during hot reload (only vite restart helps)
// and it happens after template editing (same goes for components)
let uniformList = ["u_resolution", "u_time", "u_mouse", "u_textures"];

const renameSymbol = (symbol: string, e: InputEvent) => {
  // @todo
  let val = (e.target as HTMLInputElement).value.trim();

  if (val) {
    renameMap.set(symbol, val);
  } else {
    renameMap.delete(symbol);
  }

  generateShader();
};

const copyExportContent = () => {
  return navigator.clipboard.writeText(exportContent.value).then(() => useToast(t("shader-copied")));
};

const onTextareaClick = ({ target }: PointerEvent<HTMLTextAreaElement>) => {
  const { value } = target;

  // @note reset selection first, because if you select the already selected range it will be deselected
  target.setSelectionRange(0, 0);
  target.setSelectionRange(0, value.length);

  copyExportContent();
};
</script>

<style module lang="less">
// @todo move to modal by default
.export-content {
  max-width: 50rem;

  // font-size: 1.25rem;

  & > section {
    margin-block: 3rem;
  }
}

.label {
  margin-bottom: 0.25rem;
}

.code {
  resize: none;

  box-sizing: border-box;
  width: 100%;

  min-height: 15rem;

  font-family: monospace;
  font-size: 1rem;

  cursor: pointer;

  border-radius: calc(var(--br) - var(--bw));
  background: var(--bg);

  transition: outline 300ms ease-out;
  outline: 1px transparent solid;

  &:focus-visible {
    outline: 1px var(--glow) solid;
  }
}
</style>
